import type { Location } from "history";

// Strips the namespace prefix from a resource name.
// "org.domain.project.my_resource" → "my_resource". Trailing dots are ignored.
export function stripNamespacePrefix(name: string): string {
  if (!name.includes(".")) return name;
  const last = name.split(".").pop();
  return last || name;
}

export type FilterableValue = string | null;

// Open-ended comparison operators, valid on the backend's date/time and numeric
// fields (string/bool fields reject them). Closed ranges use `range` (→ between).
export type ComparisonOperator = "lt" | "gt" | "le" | "ge";

export type Comparison = {
  operator: ComparisonOperator;
  value: string;
};

export type FieldFilters = {
  valueList: FilterableValue[];
  booleanValueList: boolean[];
  fuzzyMatch: FilterableValue;
  range: FilterableValue[];
  // One-sided comparisons, ANDed together (e.g. gt X + lt Y for an open window).
  // Optional so the many existing FieldFilters literals need no change.
  comparisons?: Comparison[];
};

export type Filters<ResourceType extends object> = Map<
  string & keyof ResourceType,
  FieldFilters
>;

export type SortDirection = "asc" | "desc";

export type Sort<ResourceType extends object> = {
  fieldName: string & keyof ResourceType;
  direction: SortDirection;
};

export type BaseQueryState<ResourceType extends object> = {
  query: {
    filters: Filters<ResourceType>;
    sort: Sort<ResourceType>;
  };
  paging: {
    offset: number;
    limit: number;
  };
  /** Filter to show only editable items (stored as simple `editable=true` in URL) */
  isEditable?: boolean;
};

export interface HydrateConfig<ResourceType extends object> {
  defaultSort: Sort<ResourceType>;
  defaultLimit?: number;
}

const DEFAULT_LIMIT = 25;

/**
 * Converts filters and sort to URLSearchParams (rich objects → flat URL strings).
 */
export function getSearchParamsForFiltersAndSort<
  ResourceType extends object,
>(opts: {
  filters: Filters<ResourceType>;
  sort: Sort<ResourceType>;
}): URLSearchParams {
  const { filters, sort } = opts;
  const searchParams = new URLSearchParams();
  for (const [fieldName, fieldFilters] of filters.entries()) {
    for (const fieldValue of fieldFilters.valueList) {
      searchParams.set(
        `filters[${fieldName}][valueList][${fieldValue ?? ""}]`,
        "true",
      );
    }
    for (const booleanValue of fieldFilters.booleanValueList) {
      searchParams.set(
        `filters[${fieldName}][booleanValueList][${booleanValue}]`,
        "true",
      );
    }
    if (fieldFilters.fuzzyMatch) {
      searchParams.set(
        `filters[${fieldName}][fuzzyMatch][${fieldFilters.fuzzyMatch}]`,
        "true",
      );
    }
    if (fieldFilters.range.length) {
      searchParams.set(
        `filters[${fieldName}][range][${fieldFilters.range}]`,
        "true",
      );
    }
    for (const { operator, value } of fieldFilters.comparisons ?? []) {
      // Encoded as "<operator>:<value>"; the value (e.g. an ISO timestamp) may
      // contain colons, so the parser splits on the first colon only.
      searchParams.set(
        `filters[${fieldName}][comparison][${operator}:${value}]`,
        "true",
      );
    }
  }
  searchParams.set("sortFieldName", sort.fieldName);
  searchParams.set("sortDirection", sort.direction);
  return searchParams;
}

/**
 * Converts full QueryState to URLSearchParams, preserving other URL params.
 */
export function dehydrateQueryStateBase<ResourceType extends object>(opts: {
  queryState: BaseQueryState<ResourceType>;
  location: Location;
}): URLSearchParams {
  const { queryState, location } = opts;
  const searchParams = new URLSearchParams(location.search.slice(1));
  const {
    query: { filters, sort },
    paging: { offset, limit },
    isEditable,
  } = queryState;
  // unclear why, but iteration is unreliable here unless we spread to an array
  for (const k of [...searchParams.keys()]) {
    if (
      k.startsWith("filters[") ||
      k.startsWith("sort[]") ||
      k === "offset" ||
      k === "limit" ||
      k === "editable"
    ) {
      searchParams.delete(k);
    }
  }
  for (const [k, v] of getSearchParamsForFiltersAndSort<ResourceType>({
    filters,
    sort,
  }).entries()) {
    searchParams.set(k, v);
  }
  searchParams.set("offset", String(offset));
  searchParams.set("limit", String(limit));
  if (isEditable) {
    searchParams.set("editable", "true");
  }
  return searchParams;
}

/**
 * Dehydrates a QueryState object to a serialized search string.
 */
export function dehydrateQueryStateToStringBase<ResourceType extends object>(
  queryState: BaseQueryState<ResourceType>,
): string {
  const params = getSearchParamsForFiltersAndSort({
    filters: queryState.query.filters,
    sort: queryState.query.sort,
  });
  params.set("offset", String(queryState.paging.offset));
  params.set("limit", String(queryState.paging.limit));
  if (queryState.isEditable) {
    params.set("editable", "true");
  }
  return params.toString();
}

/**
 * Hydrates a serialized search string into a QueryState object.
 */
export function hydrateQueryStateFromStringBase<ResourceType extends object>(
  searchString: string,
  config: HydrateConfig<ResourceType>,
): BaseQueryState<ResourceType> {
  const queryState: BaseQueryState<ResourceType> = {
    query: {
      filters: new Map(),
      sort: { ...config.defaultSort },
    },
    paging: {
      offset: 0,
      limit: config.defaultLimit ?? DEFAULT_LIMIT,
    },
    isEditable: false,
  };
  const { query, paging } = queryState;
  const filters = query.filters;
  const searchParams = new URLSearchParams(searchString);

  let collectedSortFieldName: (string & keyof ResourceType) | null = null;
  let collectedSortDirection: SortDirection | null = null;

  for (const [k, v] of [...searchParams.entries()]) {
    const filterPrefix = "filters[";
    const filterSuffix = "]";
    const filterSeparator = "][";
    if (k.startsWith(filterPrefix) && k.endsWith(filterSuffix)) {
      const parts = k
        .slice(filterPrefix.length, 0 - filterSuffix.length)
        .split(filterSeparator);
      if (parts.length === 3 && typeof parts[0] === "string") {
        const fieldName = parts[0] as string & keyof ResourceType;
        const defaultFieldFilters: FieldFilters = {
          valueList: [],
          booleanValueList: [],
          fuzzyMatch: null,
          range: [],
          comparisons: [],
        };
        if (parts[1] === "valueList" && typeof parts[2] === "string") {
          const fieldValue = parts[2];
          let fieldFilters = filters.get(fieldName);
          if (!fieldFilters) {
            fieldFilters = defaultFieldFilters;
            filters.set(fieldName, fieldFilters);
          }
          fieldFilters.valueList.push(fieldValue || null);
        } else if (
          parts[1] === "booleanValueList" &&
          (parts[2] === "true" || parts[2] === "false")
        ) {
          const booleanValue = JSON.parse(parts[2]);
          let fieldFilters = filters.get(fieldName);
          if (!fieldFilters) {
            fieldFilters = defaultFieldFilters;
            filters.set(fieldName, fieldFilters);
          }
          fieldFilters.booleanValueList.push(booleanValue);
        } else if (parts[1] === "fuzzyMatch" && typeof parts[2] === "string") {
          const fuzzyMatch = parts[2];
          let fieldFilters = filters.get(fieldName);
          if (!fieldFilters) {
            fieldFilters = defaultFieldFilters;
            filters.set(fieldName, fieldFilters);
          }
          fieldFilters.fuzzyMatch = fuzzyMatch;
        } else if (parts[1] === "range") {
          const range = parts[2].split(",");
          let fieldFilters = filters.get(fieldName);
          if (!fieldFilters) {
            fieldFilters = defaultFieldFilters;
            filters.set(fieldName, fieldFilters);
          }
          fieldFilters.range = range;
        } else if (parts[1] === "comparison") {
          // "<operator>:<value>" — split on the first colon so ISO timestamps
          // (which contain colons) survive intact in the value.
          const separatorIndex = parts[2].indexOf(":");
          const operator = parts[2].slice(0, separatorIndex);
          const value = parts[2].slice(separatorIndex + 1);
          if (
            separatorIndex > 0 &&
            (operator === "lt" ||
              operator === "gt" ||
              operator === "le" ||
              operator === "ge")
          ) {
            let fieldFilters = filters.get(fieldName);
            if (!fieldFilters) {
              fieldFilters = defaultFieldFilters;
              filters.set(fieldName, fieldFilters);
            }
            fieldFilters.comparisons = [
              ...(fieldFilters.comparisons ?? []),
              { operator, value },
            ];
          }
        }
      }
    }
    // Handle multi-column sorting with comma-separated values
    if (k === "sortFieldName" && typeof v === "string") {
      // The sortFieldName can contain comma-separated values for multi-column sorting
      if (collectedSortDirection) {
        query.sort = {
          fieldName: v as string & keyof ResourceType,
          direction: collectedSortDirection,
        };
      } else {
        collectedSortFieldName = v as string & keyof ResourceType;
      }
    }
    if (k === "sortDirection" && typeof v === "string") {
      // The sortDirection can contain comma-separated values (asc,desc,asc) for multi-column sorting
      if (collectedSortFieldName) {
        query.sort = {
          fieldName: collectedSortFieldName,
          direction: v as SortDirection,
        };
      } else {
        collectedSortDirection = v as SortDirection;
      }
    }

    if (k === "offset" && Number.isInteger(Number(v))) {
      paging.offset = Number(v);
    }
    if (k === "limit" && Number.isInteger(Number(v))) {
      paging.limit = Number(v);
    }
    if (k === "editable" && v === "true") {
      queryState.isEditable = true;
    }
  }

  return queryState;
}

/**
 * Parses URL search params from a Location object into a typed QueryState object.
 */
export function hydrateQueryStateBase<ResourceType extends object>(
  location: Location,
  config: HydrateConfig<ResourceType>,
): BaseQueryState<ResourceType> {
  // Remove the leading "?" from location.search
  const searchString = location.search.slice(1);
  return hydrateQueryStateFromStringBase(searchString, config);
}
